import { ClickAwayListener } from "@mui/material";
import type {
  ShopperAiTagId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import { useHandler } from "@redotech/react-util/hook";
import { PillTheme } from "@redotech/redo-model/pill-theme";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Dropdown } from "@redotech/redo-web/dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { ConversationTagPill as SupportTicketTagPill } from "@redotech/redo-web/support/conversation-tag-pill";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { useLiveQuery } from "@tanstack/react-db";
import { memo, useCallback, useState } from "react";
import { useAddTagSupportTicketAction } from "../actions/use-support-ticket-actions";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";
import { FieldLabel } from "./field-label";
import { TagSelectorDropdownContent } from "./tag-selector-dropdown-content";

export const TagSelector = memo(function TagSelector({
  supportTicketId,
  currentTagIds,
}: {
  supportTicketId: SupportTicketId;
  currentTagIds: readonly ShopperAiTagId[];
}) {
  const collections = useGlobalSupportTicketCollections();
  const { data: tags } = useLiveQuery(
    (q) => q.from({ tag: collections.tags }),
    [],
  );
  const addTagAction = useAddTagSupportTicketAction();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [badgeRef, setBadgeRef] = useState<HTMLElement | null>(null);

  const currentTags = tags.filter((tag) => currentTagIds.includes(tag.id));
  const hasAvailableTags = tags.length > 0;

  const handleRemoveTag = useCallback(
    (tagId: ShopperAiTagId) => {
      const newTagIds = currentTagIds.filter((id) => id !== tagId);
      void addTagAction.execute(supportTicketId, { tagIds: [...newTagIds] });
    },
    [currentTagIds, supportTicketId, addTagAction],
  );

  const handleBadgeClick = useHandler(() => {
    setDropdownOpen((open) => !open);
  });

  return (
    <Flex align="flex-start" gap="xs" justify="space-between">
      <FieldLabel>Tags</FieldLabel>
      <ClickAwayListener onClickAway={() => setDropdownOpen(false)}>
        <Flex
          align="center"
          gap="xxs"
          justify="flex-end"
          overflow="auto"
          wrap="wrap"
        >
          {currentTags.map((tag) => (
            <SupportTicketTagPill
              key={tag.id}
              tag={{
                name: tag.name,
                pillTheme: tag.pillTheme ?? PillTheme.NORMAL,
              }}
              xClicked={() => handleRemoveTag(tag.id)}
            />
          ))}
          {hasAvailableTags ? (
            <Flex cursor="pointer" onClick={handleBadgeClick} ref={setBadgeRef}>
              <RedoBadge color="gray" shadow size="sm" text="+" />
            </Flex>
          ) : (
            <Tooltip title="No ticket tags available">
              <span style={{ opacity: 0.3 }}>
                <RedoBadge color="gray" shadow size="sm" text="+" />
              </span>
            </Tooltip>
          )}
          <Dropdown
            anchor={badgeRef}
            constrainHeight
            fitToAnchor={false}
            flexProps={{ p: "none" }}
            flip={false}
            open={dropdownOpen}
            placement="bottom-end"
          >
            <TagSelectorDropdownContent
              currentTagIds={currentTagIds}
              onTagsChanged={(newTagIds) =>
                addTagAction.execute(supportTicketId, { tagIds: newTagIds })
              }
            />
          </Dropdown>
        </Flex>
      </ClickAwayListener>
    </Flex>
  );
});
